// src/services/topicPagesService.js
//
// The Topic Library's page actions — what happens to the state pages
// (portalLayouts) when a topic is added, replaced, removed or re-described.
// Every page is copied to layoutBackups/{page}__{stamp} before it changes,
// the same convention as the Duplicate topics merge and the scripts.
//
//   pagesUsingSlug(slug)                    which pages list this topic
//   addTopicToState({ topic, state, grade, standardNumber, views })
//   replaceTopicOnPages({ fromSlug, to, pageIds })   to = a library topic, or
//                                           null to take it off those pages
//   pushDescription({ slug, description, overwrite })

import { collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { exactKey } from "../utils/topicKey";
import { clearLibraryCache } from "./topicLibraryService";
import { stateName } from "../config/geography";

const VIEWS = ["student", "educator"];
const stampNow = () => new Date().toISOString().replace(/[-:]/g, "").replace(/\..*$/, "").replace("T", "-");
// media typed on a page entry wins over the library (mergeTopicWithLibrary),
// so a replaced entry drops its own — the new topic's media then show
const PAGE_MEDIA = ["videoUrl", "video", "musicUrl", "music", "singAlongUrl", "lyrics", "imageUrl", "titleImage"];

/* ---------- reading every page, once in a while ---------- */

let pagesCache = null; // { at, pages: [{ id, data }] }
const CACHE_MS = 60 * 1000;

export async function allPages({ fresh = false } = {}) {
  if (!fresh && pagesCache && Date.now() - pagesCache.at < CACHE_MS) return pagesCache.pages;
  const snap = await getDocs(collection(db, "portalLayouts"));
  const pages = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
  pagesCache = { at: Date.now(), pages };
  return pages;
}
export const forgetPages = () => {
  pagesCache = null;
};

/** pages whose topic list has this slug: [{ id, state, grade, subject, view, entry }] */
export async function pagesUsingSlug(slug, opts) {
  const key = exactKey(slug);
  if (!key) return [];
  const pages = await allPages(opts);
  const out = [];
  pages.forEach(({ id, data }) => {
    const entry = (data.topics || []).find((t) => exactKey(t?.slug) === key);
    if (!entry) return;
    const [state, grade, subject, view] = id.split("_");
    out.push({ id, state, grade, subject, view, entry });
  });
  return out.sort((a, b) => a.id.localeCompare(b.id));
}

/* ---------- topicUsage/_all (the States column) ---------- */

async function patchUsage(fn) {
  try {
    const ref = doc(db, "topicUsage", "_all");
    const snap = await getDoc(ref);
    if (!snap.exists()) return;
    const usage = { ...(snap.data().usage || {}) };
    fn(usage);
    await updateDoc(ref, {
      usage,
      usedCount: Object.values(usage).filter((u) => u && u.stateCount > 0).length,
      lastAttachAt: new Date().toISOString(),
    });
  } catch {
    /* the column just counts on the next audit */
  }
}

function addUsage(usage, slug, state, grade, subject) {
  const entry = usage[slug] || { stateCount: 0, states: [] };
  const byState = new Map((entry.states || []).map((s) => [s.state, s]));
  const st = String(state).toLowerCase();
  const was = byState.get(st);
  byState.set(st, {
    state: st,
    stateName: was?.stateName || stateName(st),
    grades: [...new Set([...(was?.grades || []), grade])].sort(),
    subjects: [...new Set([...(was?.subjects || []), subject])].sort(),
  });
  const states = [...byState.values()].sort((a, b) => String(a.stateName).localeCompare(String(b.stateName)));
  usage[slug] = { stateCount: states.length, states };
}

/* ---------- add one topic to one state's page ---------- */

/**
 * Puts the topic on {state}_{grade}_{subject}_student and _educator (made if
 * missing). Media are left to the library. Already there → its standard
 * number is updated instead.
 */
export async function addTopicToState({ topic, state, grade, standardNumber = "", views = VIEWS }) {
  const st = String(state || "").toLowerCase();
  const gr = String(grade || "").toLowerCase();
  const subject = topic.subject || "science";
  if (!st || !gr || !topic?.slug) throw new Error("Pick a state and a grade.");
  const code = String(standardNumber || "").trim();
  const stamp = stampNow();
  const result = { added: [], updated: [], created: [] };

  // a page made new borrows its buttons from the other view, if that exists
  const sibling = {};
  for (const view of views) {
    // eslint-disable-next-line no-await-in-loop
    const s = await getDoc(doc(db, "portalLayouts", `${st}_${gr}_${subject}_${view}`));
    sibling[view] = s.exists() ? s.data() : null;
  }

  for (const view of views) {
    const id = `${st}_${gr}_${subject}_${view}`;
    const ref = doc(db, "portalLayouts", id);
    const data = sibling[view];
    const key = exactKey(topic.slug);
    if (data) {
      const topics = [...(data.topics || [])];
      const at = topics.findIndex((t) => exactKey(t?.slug) === key);
      if (at >= 0) {
        if (!code || topics[at].standardNumber === code) continue;
        topics[at] = { ...topics[at], standardNumber: code, standardNumbers: [...new Set([code, ...(topics[at].standardNumbers || [])])] };
        // eslint-disable-next-line no-await-in-loop
        await setDoc(doc(db, "layoutBackups", `${id}__${stamp}`), data);
        // eslint-disable-next-line no-await-in-loop
        await updateDoc(ref, { topics });
        result.updated.push(id);
        continue;
      }
      topics.push({
        slug: topic.slug,
        title: topic.title || topic.slug,
        standardNumber: code,
        standardNumbers: code ? [code] : [],
        description: "",
        visible: true,
        order: topics.length + 1,
      });
      // eslint-disable-next-line no-await-in-loop
      await setDoc(doc(db, "layoutBackups", `${id}__${stamp}`), data);
      // eslint-disable-next-line no-await-in-loop
      await updateDoc(ref, { topics });
      result.added.push(id);
    } else {
      const other = sibling[view === "student" ? "educator" : "student"];
      // eslint-disable-next-line no-await-in-loop
      await setDoc(ref, {
        state: st,
        grade: gr,
        subject,
        view,
        title: other?.title || `${stateName(st)} ${gr === "k" ? "Kindergarten" : `${gr} Grade`} ${subject === "socialstudies" ? "Social Studies" : subject.replace(/^./, (c) => c.toUpperCase())}`,
        theme: other?.theme || "kid",
        ...(other?.tools ? { tools: other.tools } : {}),
        topicOrder: "standard",
        topics: [
          {
            slug: topic.slug,
            title: topic.title || topic.slug,
            standardNumber: code,
            standardNumbers: code ? [code] : [],
            description: "",
            visible: true,
            order: 1,
          },
        ],
        createdFrom: "Topic library ▸ Add to state",
      });
      result.created.push(id);
    }
  }
  if (result.added.length || result.created.length) {
    await patchUsage((usage) => addUsage(usage, topic.slug, st, gr, subject));
  }
  forgetPages();
  clearLibraryCache();
  return { ...result, stamp };
}

/* ---------- replace (or remove) a topic on pages ---------- */

export async function replaceTopicOnPages({ fromSlug, to = null, pageIds }) {
  const from = exactKey(fromSlug);
  const toKey = to ? exactKey(to.slug) : "";
  if (!from) throw new Error("No topic to replace.");
  if (to && toKey === from) throw new Error("Pick a different topic to put in its place.");
  const stamp = stampNow();
  const ids = pageIds || (await pagesUsingSlug(fromSlug, { fresh: true })).map((p) => p.id);
  let changed = 0;
  let removed = 0;
  let replaced = 0;
  const touched = [];
  for (const id of ids) {
    const ref = doc(db, "portalLayouts", id);
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const data = snap.data();
    const hasTarget = to && (data.topics || []).some((t) => exactKey(t?.slug) === toKey);
    let didChange = false;
    const next = [];
    (data.topics || []).forEach((t) => {
      if (exactKey(t?.slug) !== from) {
        next.push(t);
        return;
      }
      didChange = true;
      if (!to || hasTarget) {
        removed += 1; // no replacement, or the page already has the new one
        return;
      }
      const swapped = { ...t, slug: to.slug, title: to.title || t.title };
      PAGE_MEDIA.forEach((f) => delete swapped[f]);
      next.push(swapped);
      replaced += 1;
    });
    if (!didChange) continue;
    // eslint-disable-next-line no-await-in-loop
    await setDoc(doc(db, "layoutBackups", `${id}__${stamp}`), data);
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(ref, { topics: next.map((t, i) => ({ ...t, order: i + 1 })) });
    changed += 1;
    touched.push(id);
  }
  await patchUsage((usage) => {
    const old = usage[fromSlug] || usage[from];
    delete usage[fromSlug];
    delete usage[from];
    if (to && old?.states) {
      touched.forEach((id) => {
        const [state, grade, subject] = id.split("_");
        addUsage(usage, to.slug, state, grade, subject);
      });
    }
  });
  forgetPages();
  clearLibraryCache();
  return { changed, replaced, removed, stamp };
}

/* ---------- description onto pages ---------- */

/**
 * Pages with no description of their own already show the library's. With
 * overwrite, pages that DO have their own wording get the new one too.
 */
export async function pushDescription({ slug, description, overwrite = false }) {
  const key = exactKey(slug);
  const pages = await pagesUsingSlug(slug, { fresh: true });
  const stamp = stampNow();
  let changed = 0;
  let keptOwn = 0;
  for (const p of pages) {
    const own = String(p.entry.description || "").trim();
    if (!own) continue; // shows the library's already
    if (!overwrite) {
      keptOwn += 1;
      continue;
    }
    if (own === description) continue;
    const ref = doc(db, "portalLayouts", p.id);
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDoc(ref);
    if (!snap.exists()) continue;
    const data = snap.data();
    const topics = (data.topics || []).map((t) => (exactKey(t?.slug) === key ? { ...t, description } : t));
    // eslint-disable-next-line no-await-in-loop
    await setDoc(doc(db, "layoutBackups", `${p.id}__${stamp}`), data);
    // eslint-disable-next-line no-await-in-loop
    await updateDoc(ref, { topics });
    changed += 1;
  }
  forgetPages();
  return { pages: pages.length, changed, keptOwn };
}
